import React, { useState } from "react";
import { useParams } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { useSnackbar } from "notistack";
import { useTheme } from "@mui/material/styles";
import {
  Box,
  Button,
  ButtonGroup,
  Modal,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import ImageIcon from "@mui/icons-material/Image";
import LinkIcon from "@mui/icons-material/Link";

import {
  toggleMediaModal,
  selectSelectedMedia,
  setSelectedMedia,
} from "../../../redux/slices/viewSlice";
import RightCloseIcon from "../../../modules/assets/RightCloseIcon";
import { invalidateCollectionCache, useCollection } from "../../../api/fetchCollectionData";
import { createStory, updateStoryBlockWithNewStory } from "../../../redux/api/storyBlockAPI";

const style = {
  topMargin: {
    marginTop: 1,
  },
  box: {
    bgcolor: "background.paper",
    boxShadow: 24,
    display: "flex",
    flexDirection: "column",
    height: 525,
    justifyContent: "space-between",
    left: "50%",
    p: 3,
    position: "absolute",
    top: "50%",
    transform: "translate(-50%, -50%)",
    width: 600,
  },
};

export function AddStoryModal({ open, onClose, onStoryCreated }) {
  const dispatch = useDispatch();
  const theme = useTheme();
  const { storyBlockId } = useParams();
  const { enqueueSnackbar } = useSnackbar();
  const {collection: storyBlock} = useCollection(storyBlockId);
  const selectedMedia = useSelector(selectSelectedMedia);

  const [type, setType] = useState("multimedia");
  const [storyTitle, setStoryTitle] = useState('');
  const [navigationLink, setNavigationLink] = useState('');

  const handleClose = () => {
    setType("multimedia");
    setStoryTitle("");
    setNavigationLink("");
    dispatch(setSelectedMedia({}));
    onClose();
  };

  const onAddStoryClick = async () => {
    let data = {
      thumbnail_text: storyTitle,
      internal_name: storyTitle,
      internal_description: "",
      alt_text: storyTitle,
      asset_id: selectedMedia.id,
      type: type,
      link_url: navigationLink ? navigationLink : "",
    };

    const storyIds = storyBlock?.stories?.map((s) => s.id);

    try {
      const newStory = await createStory(data);
      storyIds.push(newStory.id);

      // Now add the new story to the story block
      await updateStoryBlockWithNewStory({id: storyBlockId, newStoryIds: storyIds});

      invalidateCollectionCache();

      // Show the story as selected
      onStoryCreated(newStory);

      enqueueSnackbar("Saved Story", {variant: "success"});
    } catch(error) {
      enqueueSnackbar("Failed to add story", {variant: "error"});
    }

    handleClose();
  }

  const renderThumbnail = () => {
    if (selectedMedia?.id) {
      return (
        <Button
          onClick={() => dispatch(toggleMediaModal({ type: "images" }))}
          sx={{ "&:hover": { backgroundColor: "transparent" } }}
        >
          <img
            src={selectedMedia.image_square_crop_url}
            alt="story thumbnail"
            style={{
              maxHeight: "72px",
              maxWidth: "72px",
              borderRadius: "50%",
              marginBottom: "20px",
            }}
          />
        </Button>
      );
    }
    return (
      <Button
        sx={{
          height: "72px",
          width: "72px",
          backgroundColor: theme.palette.grey[200],
          borderRadius: "50%",
          marginBottom: "20px",
          "& .MuiSvgIcon-root": {
            color: theme.palette.grey[500],
          },
          "&:hover .MuiSvgIcon-root": {
            color: theme.palette.primary.main,
          },
        }}
        onClick={() => dispatch(toggleMediaModal({ type: "images" }))}
      >
        <ImageIcon />
      </Button>
    );
  };

  const disabledState = () => {
    const url = selectedMedia?.id;

    if (type === "multimedia") {
      if (!url || storyTitle === "") {
        return true;
      }
    } else if (type === "link") {
      if (!url || storyTitle === "" || navigationLink === "") {
        return true;
      }
    }
    return false;
  };

  return (
    <Modal open={open} onClose={handleClose}>
      <Box sx={style.box}>
        <Typography variant="h5">
          New Story
          <RightCloseIcon onClick={handleClose} />
        </Typography>
        <ButtonGroup variant="outlined" fullWidth>
          <Button
            variant={type === "multimedia" ? "contained" : "outlined"}
            startIcon={<ImageIcon />}
            onClick={() => setType("multimedia")}
          >
            Multimedia
          </Button>
          <Button
            variant={type === "link" ? "contained" : "outlined"}
            startIcon={<LinkIcon />}
            onClick={() => setType("link")}
          >
            Navigation
          </Button>
        </ButtonGroup>
        <Typography variant="body2">
          Tapping the thumbnail will open your story full-screen. You'll add the
          story's chapters in the next step.
        </Typography>
        <Stack alignItems="center">
          {renderThumbnail()}
          <TextField
            label="Story Title"
            helperText="Keep this to 1-2 words (about 15 characters)"
            value={storyTitle}
            onChange={(e) => setStoryTitle(e.target.value)}
          />
          {type === "link" && (
            <TextField
              label="Link"
              helperText="Enter the URL of the page you want to link to"
              value={navigationLink}
              onChange={(e) => setNavigationLink(e.target.value)}
              sx={{ mt: 1 }}
            />
          )}
        </Stack>
        <Stack direction="row" justifyContent="end" sx={style.topMargin}>
          <Button variant="outlined" onClick={handleClose}>
            Cancel
          </Button>
          <Button
            variant="contained"
            sx={{ ml: 1 }}
            disabled={disabledState()}
            onClick={onAddStoryClick}
          >
            Add Story
          </Button>
        </Stack>
      </Box>
    </Modal>
  );
}
